import type { Token, Tokens } from "marked";

import { lexMessage, sanitizeLinkHref } from "~/lib/markdown-render";

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

interface Out {
  formatted: boolean;
}

function inline(tokens: Token[] | undefined, out: Out): string {
  return (tokens ?? [])
    .map((token) => {
      switch (token.type) {
        case "strong":
          out.formatted = true;
          return `<strong>${inline(token.tokens, out)}</strong>`;
        case "em":
          out.formatted = true;
          return `<em>${inline(token.tokens, out)}</em>`;
        case "del":
          out.formatted = true;
          return `<del>${inline(token.tokens, out)}</del>`;
        case "codespan":
          out.formatted = true;
          return `<code>${escapeHtml(token.text)}</code>`;
        case "br":
          return "<br />";
        case "link": {
          const href = sanitizeLinkHref(token.href);
          // Bare URLs stay text: every client autolinks them, so they add no formatting.
          if (!href || token.raw === token.href) return escapeHtml(token.text);
          out.formatted = true;
          return `<a href="${escapeHtml(href)}">${inline(token.tokens, out)}</a>`;
        }
        case "text":
          return "tokens" in token && token.tokens
            ? inline(token.tokens, out)
            : escapeHtml(token.text);
        default:
          return escapeHtml("text" in token ? String(token.text) : String(token.raw));
      }
    })
    .join("");
}

function blocks(tokens: Token[], out: Out): string {
  // Inline parts (paragraph text) need a blank line between them; block elements don't.
  const parts: Array<{ html: string; block: boolean }> = [];
  for (const token of tokens) {
    switch (token.type) {
      case "space":
      case "def":
        break;
      case "paragraph":
        parts.push({ html: inline(token.tokens, out), block: false });
        break;
      case "text":
        parts.push({
          html: token.tokens ? inline(token.tokens, out) : escapeHtml(token.text),
          block: false,
        });
        break;
      case "heading": {
        out.formatted = true;
        const level = Math.min(Math.max(token.depth, 1), 6);
        parts.push({ html: `<h${level}>${inline(token.tokens, out)}</h${level}>`, block: true });
        break;
      }
      case "code": {
        out.formatted = true;
        const lang = /^[\w+-]+$/.test(token.lang ?? "") ? ` class="language-${token.lang}"` : "";
        parts.push({
          html: `<pre><code${lang}>${escapeHtml(token.text)}</code></pre>`,
          block: true,
        });
        break;
      }
      case "blockquote":
        out.formatted = true;
        parts.push({
          html: `<blockquote>${blocks(token.tokens ?? [], out)}</blockquote>`,
          block: true,
        });
        break;
      case "list": {
        out.formatted = true;
        const list = token as Tokens.List;
        const tag = list.ordered ? "ol" : "ul";
        const start =
          list.ordered && list.start !== "" && list.start !== 1 ? ` start="${list.start}"` : "";
        const items = list.items
          .map((item) => {
            const body = blocks(
              item.tokens.filter((t) => t.type !== "checkbox"),
              out,
            );
            const box = item.task ? (item.checked ? "☑ " : "☐ ") : "";
            return `<li>${box}${body}</li>`;
          })
          .join("");
        parts.push({ html: `<${tag}${start}>${items}</${tag}>`, block: true });
        break;
      }
      case "table": {
        out.formatted = true;
        const table = token as Tokens.Table;
        const head = table.header.map((cell) => `<th>${inline(cell.tokens, out)}</th>`).join("");
        const rows = table.rows
          .map(
            (row) =>
              `<tr>${row.map((cell) => `<td>${inline(cell.tokens, out)}</td>`).join("")}</tr>`,
          )
          .join("");
        parts.push({
          html: `<table><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>`,
          block: true,
        });
        break;
      }
      case "hr":
        out.formatted = true;
        parts.push({ html: "<hr />", block: true });
        break;
      default:
        parts.push({ html: escapeHtml(String(token.raw).trim()), block: false });
    }
  }
  return parts
    .map((part, i) => {
      const prev = parts[i - 1];
      return prev && !prev.block && !part.block ? `<br /><br />${part.html}` : part.html;
    })
    .join("");
}

/**
 * Matrix `org.matrix.custom.html` for a composer body, or `null` when the
 * body has no markdown, so plain text never grows a redundant formatted_body.
 * Uses the same lexer Chat renders with, so senders and readers agree.
 */
export function markdownToMatrixHtml(input: string): string | null {
  const out: Out = { formatted: false };
  const html = blocks(lexMessage(input), out);
  return out.formatted ? html : null;
}
